import { useForm } from "react-hook-form";
import { FormModal, Input, Textarea } from "@marekl/adminer/components";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { useUpdateTemplate, type TemplateDetail } from "../../lib/queries/templates";

interface DetailsFormValues {
  name: string;
  description: string;
}

/** Edits a template's display name and description; the key never changes. */
export function TemplateDetailsModal({
  template,
  onClose,
}: {
  template: TemplateDetail;
  onClose: () => void;
}) {
  const update = useUpdateTemplate(template.id);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<DetailsFormValues>({
    mode: "onTouched",
    defaultValues: { name: template.name, description: template.description ?? "" },
  });

  async function onSubmit(values: DetailsFormValues) {
    try {
      await update.mutateAsync({
        name: values.name.trim(),
        description: values.description.trim(),
      });
      notifySuccess("The template details were saved.");
      onClose();
    } catch (err) {
      apiErrorToFormErrors<DetailsFormValues>(err, setError, {
        fallbackMessage: "Saving failed. Please try again.",
      });
    }
  }

  return (
    <FormModal
      open
      title="Template details"
      onClose={onClose}
      busy={update.isPending}
      error={rootErrorMessage(errors)}
      submitLabel="Save"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Input
        label="Name"
        required
        hint={`Shown in the admin and in client template pickers (e.g. WordPress). The key “${template.key}” stays the same.`}
        error={errors.name?.message}
        {...register("name", {
          validate: (v) => v.trim() !== "" || "Required field",
          maxLength: { value: 128, message: "At most 128 characters" },
        })}
      />
      <Textarea
        label="Description"
        error={errors.description?.message}
        {...register("description", {
          maxLength: { value: 512, message: "At most 512 characters" },
        })}
      />
    </FormModal>
  );
}
